import type { PocBlock } from '@terra/shared-schemas-orchestrator';

function identityKey(block: PocBlock): string | null {
  if (block.type === 'image') {
    return `image:${block.imageId}`;
  }

  if (block.type !== 'evidence') {
    return null;
  }

  if (block.toolExecutionHarId?.trim()) {
    return `har:${block.toolExecutionHarId}`;
  }

  if (block.evidenceRef) {
    return `ref:${block.evidenceRef.source}:${block.evidenceRef.id}`;
  }

  return null;
}

function narrativeBlocks(blocks: PocBlock[]): PocBlock[] {
  return blocks.filter((block) => block.type === 'narrative');
}

function isUnkeyedEvidence(block: PocBlock): boolean {
  return block.type === 'evidence' && identityKey(block) === null;
}

function unkeyedStableKey(block: PocBlock): string {
  if (block.type !== 'evidence') {
    return '';
  }

  return [
    block.evidenceType ?? '',
    block.manualRequest ?? '',
    block.manualResponse ?? '',
  ].join('\0');
}

function narrativeContents(blocks: PocBlock[]): string[] {
  return narrativeBlocks(blocks).flatMap((block) =>
    block.type === 'narrative' ? [block.content] : [],
  );
}

function draftNarrativesAreInStaged({
  draft,
  staged,
}: {
  draft: PocBlock[];
  staged: PocBlock[];
}): boolean {
  const available = narrativeBlocks(staged).flatMap((block) =>
    block.type === 'narrative' ? [block.content] : [],
  );

  return narrativeBlocks(draft).every((block) => {
    if (block.type !== 'narrative') {
      return true;
    }

    const matchIndex = available.indexOf(block.content);

    if (matchIndex === -1) {
      return false;
    }

    available.splice(matchIndex, 1);

    return true;
  });
}

export function mergeStagedPocBlocksOntoEditorDraft({
  draft,
  saved,
  staged,
}: {
  draft: PocBlock[];
  saved: PocBlock[];
  staged: PocBlock[];
}): PocBlock[] {
  if (draft.length === 0 || draftNarrativesAreInStaged({ draft, staged })) {
    return staged;
  }

  const stagedByKey = new Map<string, PocBlock>();

  for (const block of staged) {
    const key = identityKey(block);

    if (key) {
      stagedByKey.set(key, block);
    }
  }

  const savedNarratives = narrativeBlocks(saved);
  const stagedNarratives = narrativeBlocks(staged);
  const draftNarratives = draft.flatMap((block, index) =>
    block.type === 'narrative' ? [{ block, index }] : [],
  );
  const narrativeReplacements = new Map<number, PocBlock>();
  const usedDraftNarrativeIndexes = new Set<number>();

  savedNarratives.forEach((savedBlock, savedIndex) => {
    const stagedBlock = stagedNarratives[savedIndex];

    if (
      !stagedBlock ||
      stagedBlock.type !== 'narrative' ||
      savedBlock.type !== 'narrative' ||
      stagedBlock.content === savedBlock.content
    ) {
      return;
    }

    const match = draftNarratives.find(
      (entry) =>
        entry.block.type === 'narrative' &&
        entry.block.content === savedBlock.content &&
        !usedDraftNarrativeIndexes.has(entry.index),
    );
    const targetIndex = match?.index ?? draftNarratives[savedIndex]?.index;

    if (targetIndex === undefined) {
      return;
    }

    usedDraftNarrativeIndexes.add(targetIndex);
    narrativeReplacements.set(targetIndex, stagedBlock);
  });

  const usedKeys = new Set<string>();
  const merged = draft.map((block, index) => {
    const replacement = narrativeReplacements.get(index);

    if (replacement) {
      return replacement;
    }

    const key = identityKey(block);

    if (!key) {
      return block;
    }

    const stagedBlock = stagedByKey.get(key);

    if (!stagedBlock) {
      return block;
    }

    usedKeys.add(key);

    return stagedBlock;
  });

  const stagedUnkeyed = staged.filter(isUnkeyedEvidence).map((block) => ({
    block,
    used: false,
  }));
  const omittedDraftIndexes = new Set<number>();
  const consumedDraftIndexes = new Set<number>();

  saved.filter(isUnkeyedEvidence).forEach((savedBlock) => {
    const stableKey = unkeyedStableKey(savedBlock);
    const stagedMatch = stagedUnkeyed.find(
      (entry) => !entry.used && unkeyedStableKey(entry.block) === stableKey,
    );
    const draftIndex = merged.findIndex(
      (block, index) =>
        !consumedDraftIndexes.has(index) &&
        isUnkeyedEvidence(block) &&
        unkeyedStableKey(block) === stableKey,
    );

    if (stagedMatch) {
      stagedMatch.used = true;
    }

    if (draftIndex === -1) {
      return;
    }

    consumedDraftIndexes.add(draftIndex);

    if (!stagedMatch) {
      omittedDraftIndexes.add(draftIndex);

      return;
    }

    merged[draftIndex] = stagedMatch.block;
  });

  const kept = merged.filter((_, index) => !omittedDraftIndexes.has(index));
  const remainingNarratives = narrativeContents(kept);
  const extraNarratives = narrativeBlocks(staged)
    .slice(narrativeBlocks(saved).length)
    .filter((block) => {
      if (block.type !== 'narrative') {
        return false;
      }

      const matchIndex = remainingNarratives.indexOf(block.content);

      if (matchIndex === -1) {
        return true;
      }

      remainingNarratives.splice(matchIndex, 1);

      return false;
    });

  kept.push(
    ...stagedUnkeyed.filter((entry) => !entry.used).map((entry) => entry.block),
    ...extraNarratives,
  );

  for (const block of staged) {
    const key = identityKey(block);

    if (!key || usedKeys.has(key)) {
      continue;
    }

    const draftHasKey = draft.some(
      (draftBlock) => identityKey(draftBlock) === key,
    );

    if (!draftHasKey) {
      kept.push(block);
    }
  }

  return kept;
}
